// CHUNK 11: Admin forecast metrics dashboard at /admin-ap/forecast-metrics
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface QualityRow {
  forecast_type:     string;
  total_cached:      number;
  total_feedback:    number;
  avg_rating:        number | null;
  positive_feedback: number;
  negative_feedback: number;
  unique_readers:    number;
  total_views:       number;
  total_reads:       number;
  pregenerated_count: number;
}

interface PregenLog {
  id:                  string;
  run_at:              string;
  forecast_type:       string;
  users_targeted:      number;
  forecasts_generated: number;
  forecasts_skipped:   number;
  errors:              number;
  duration_ms:         number | null;
}

function KpiCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="border border-[#C9A84C]/20 p-5 rounded-sm bg-[#0D1130]/40">
      <div className="text-[#F5ECD7]/50 text-xs uppercase tracking-widest mb-1">{label}</div>
      <div className="text-[#C9A84C] text-3xl font-light">{value}</div>
      {sub && <div className="text-[#F5ECD7]/40 text-xs mt-1">{sub}</div>}
    </div>
  );
}

function pct(num: number, den: number): string {
  if (!den) return '-';
  return `${((num / den) * 100).toFixed(1)}%`;
}

export function ForecastMetricsPage() {
  const [quality,  setQuality]  = useState<QualityRow[]>([]);
  const [logs,     setLogs]     = useState<PregenLog[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [qRes, lRes] = await Promise.all([
        supabase.from('forecast_quality_metrics').select('*'),
        supabase
          .from('pregeneration_logs')
          .select('*')
          .order('run_at', { ascending: false })
          .limit(30),
      ]);

      if (qRes.error) { setError(qRes.error.message); setLoading(false); return; }
      if (lRes.error) { setError(lRes.error.message); setLoading(false); return; }

      setQuality((qRes.data ?? []) as QualityRow[]);
      setLogs((lRes.data ?? []) as PregenLog[]);
      setLoading(false);
    }
    load();
  }, []);

  const totals = quality.reduce(
    (acc, r) => ({
      cached:    acc.cached    + (r.total_cached ?? 0),
      feedback:  acc.feedback  + (r.total_feedback ?? 0),
      views:     acc.views     + (r.total_views ?? 0),
      reads:     acc.reads     + (r.total_reads ?? 0),
      pregen:    acc.pregen    + (r.pregenerated_count ?? 0),
      positive:  acc.positive  + (r.positive_feedback ?? 0),
    }),
    { cached: 0, feedback: 0, views: 0, reads: 0, pregen: 0, positive: 0 },
  );

  const lastLog   = logs[0];
  const hitRate   = pct(totals.pregen, totals.cached);
  const readRate  = pct(totals.reads, totals.views);
  const thumbsUp  = pct(totals.positive, totals.feedback);

  return (
    <div className="p-8 text-[#F5ECD7]">
      <div className="mb-8">
        <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-1">
          Métricas de Previsões
        </h1>
        <p className="text-[#F5ECD7]/50 text-sm">Cache, pré-geração, leitura e feedback</p>
      </div>

      {loading && (
        <div className="text-[#F5ECD7]/50 text-sm">Carregando…</div>
      )}

      {error && (
        <div className="mb-6 p-4 border border-red-500/40 bg-red-900/20 text-red-300 text-sm rounded-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <KpiCard label="Total em cache"   value={totals.cached}   sub="todos os períodos" />
            <KpiCard label="Pré-geradas"      value={totals.pregen}   sub={`${hitRate} do total`} />
            <KpiCard label="Taxa de leitura"  value={readRate}        sub={`${totals.reads} lidas`} />
            <KpiCard label="Avaliação positiva" value={thumbsUp}     sub={`${totals.feedback} avaliações`} />
          </div>

          {/* Last cron run */}
          {lastLog && (
            <div className="mb-8 p-5 border border-[#C9A84C]/20 rounded-sm bg-[#0D1130]/40">
              <h2 className="text-[#F5ECD7]/70 text-xs uppercase tracking-widest mb-3">
                Última pré-geração ({lastLog.forecast_type})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                {[
                  { label: 'Alvo',      value: lastLog.users_targeted },
                  { label: 'Geradas',   value: lastLog.forecasts_generated },
                  { label: 'Puladas',   value: lastLog.forecasts_skipped },
                  { label: 'Erros',     value: lastLog.errors },
                  { label: 'Duração',   value: lastLog.duration_ms ? `${lastLog.duration_ms}ms` : '-' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-[#F5ECD7]/40 text-xs mb-1">{label}</div>
                    <div className="text-[#C9A84C]">{value}</div>
                  </div>
                ))}
              </div>
              <div className="text-[#F5ECD7]/30 text-xs mt-3">
                {new Date(lastLog.run_at).toLocaleString('pt-BR')}
              </div>
            </div>
          )}

          {/* Quality by type */}
          <div className="mb-10">
            <h2 className="text-[#F5ECD7]/70 text-xs uppercase tracking-widest mb-4">
              Qualidade por período
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[#C9A84C]/20">
                    {['Período', 'Cache', 'Pré-geradas', 'Visualizações', 'Leituras', 'Feedback', 'Nota média'].map(h => (
                      <th key={h} className="pb-3 pr-6 text-[#F5ECD7]/40 text-xs uppercase tracking-wider font-normal">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {quality.map(row => (
                    <tr key={row.forecast_type} className="border-b border-[#C9A84C]/10">
                      <td className="py-3 pr-6 text-[#C9A84C] capitalize">{row.forecast_type}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{row.total_cached}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{row.pregenerated_count}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{row.total_views}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{row.total_reads}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{row.total_feedback}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">
                        {row.avg_rating != null ? row.avg_rating.toFixed(2) : '-'}
                      </td>
                    </tr>
                  ))}
                  {quality.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-6 text-[#F5ECD7]/30 text-center text-sm">
                        Sem dados ainda
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pre-generation log */}
          <div>
            <h2 className="text-[#F5ECD7]/70 text-xs uppercase tracking-widest mb-4">
              Histórico de pré-geração (30 execuções)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[#C9A84C]/20">
                    {['Data', 'Tipo', 'Alvo', 'Geradas', 'Puladas', 'Erros', 'Duração'].map(h => (
                      <th key={h} className="pb-3 pr-6 text-[#F5ECD7]/40 text-xs uppercase tracking-wider font-normal">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className="border-b border-[#C9A84C]/10">
                      <td className="py-2 pr-6 text-[#F5ECD7]/60 text-xs">
                        {new Date(log.run_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="py-2 pr-6 text-[#C9A84C] capitalize">{log.forecast_type}</td>
                      <td className="py-2 pr-6 text-[#F5ECD7]/80">{log.users_targeted}</td>
                      <td className="py-2 pr-6 text-[#F5ECD7]/80">{log.forecasts_generated}</td>
                      <td className="py-2 pr-6 text-[#F5ECD7]/80">{log.forecasts_skipped}</td>
                      <td className={`py-2 pr-6 ${log.errors > 0 ? 'text-red-400' : 'text-[#F5ECD7]/80'}`}>
                        {log.errors}
                      </td>
                      <td className="py-2 pr-6 text-[#F5ECD7]/60">{log.duration_ms ? `${log.duration_ms}ms` : '-'}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-6 text-[#F5ECD7]/30 text-center text-sm">
                        Nenhuma execução registrada ainda
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
